// src/components/settings/SystemGuideTab.tsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
  AccountTree as ArchitectureIcon,
  Lock as AuthIcon,
  Email as EmailIcon,
  Receipt as InvoiceIcon,
  Cloud as CloudIcon,
  Visibility as VisibilityIcon,
  Security as SecurityIcon,
  GppGoodOutlined as ShieldIcon,
} from "@mui/icons-material";

const sectionSx = {
  "&::before": { display: "none" },
  boxShadow: "none",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "12px !important",
  mb: 2,
  overflow: "hidden",
};

const headerSx = {
  "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1.5 },
};

const SystemGuideTab: React.FC = () => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (_: unknown, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        System Guide
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Reference documentation for LocateNow Pro features, data pipeline, and
        admin functionality. Intended for QA and system administration.
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 2 }}
      >
        Last verified: April 2026 &middot; commit 29ca994
      </Typography>

      {/* ── Architecture Overview ── */}
      <Accordion
        expanded={expanded === "architecture"}
        onChange={handleChange("architecture")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <ArchitectureIcon color="primary" />
          <Typography fontWeight={600}>Architecture Overview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            LocateNow Pro is a full-stack application for apartment locators.
            The frontend is a React SPA that communicates with a Python FastAPI
            backend. All persistent data lives in Supabase (hosted PostgreSQL).
            Heavy tasks are offloaded to AWS.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            System components
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Component</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Technology</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Frontend</TableCell>
                  <TableCell>React + Material UI</TableCell>
                  <TableCell>
                    Single-page app for agents and admins
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Backend API</TableCell>
                  <TableCell>Python FastAPI + Uvicorn</TableCell>
                  <TableCell>
                    REST API (~64 endpoints) handling all business logic
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Database</TableCell>
                  <TableCell>Supabase (PostgreSQL)</TableCell>
                  <TableCell>
                    All persistent data, auth, and row-level security
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Scraper Lambda</TableCell>
                  <TableCell>AWS Lambda (headless Chromium)</TableCell>
                  <TableCell>
                    On-demand property data refresh
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Invoice Lambda</TableCell>
                  <TableCell>AWS Lambda</TableCell>
                  <TableCell>
                    PDF invoice generation
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>File Storage</TableCell>
                  <TableCell>AWS S3</TableCell>
                  <TableCell>
                    Property images and invoice PDFs
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Gmail OAuth</TableCell>
                  <TableCell>
                    All outbound email &mdash; listing emails, guest cards,
                    lease emails, and invoices. Open and click tracking
                    attached automatically.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            Data flow summary
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Search &amp; Send:</strong> Agent searches properties
            &rarr; selects matches &rarr; freshness guard checks data age
            &rarr; email sent
            via the agent's connected Gmail with open and click tracking
            attached automatically.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>Scraping:</strong> Local batch (weekly, real Chrome) or
            Lambda (on-demand, headless) scrapes apartment websites &rarr;
            writes floor plans, units, pricing to Supabase &rarr; updates
            scrape metadata for freshness tracking.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ── Authentication & Roles ── */}
      <Accordion
        expanded={expanded === "auth"}
        onChange={handleChange("auth")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <AuthIcon color="primary" />
          <Typography fontWeight={600}>Authentication & Roles</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Authentication is handled by Supabase Auth. Users sign in via
            email/password and receive a JWT token. The backend validates this
            token on every request and enforces role-based access.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            User roles
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Access</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Chip label="Admin" size="small" color="warning" />
                  </TableCell>
                  <TableCell>
                    Full access: Admin Dashboard, property editing, Lambda
                    refresh, data export, scrape guard, System Guide, and all
                    agent features
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="Agent" size="small" color="info" />
                  </TableCell>
                  <TableCell>
                    Search, client management, saved searches, sending listings,
                    invoices, Gmail connection
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="Demo" size="small" />
                  </TableCell>
                  <TableCell>
                    Restricted sandbox: 3 seeded clients, demo-visible
                    properties only. No email sending, no invoice creation, no
                    settings access, no market insights. Data resets via admin
                    endpoint.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Access scope
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each user profile includes an <code>access_scope</code> field:{" "}
            <code>production</code> (default) or <code>sandbox</code>. Backend
            sandbox guardrails check <code>access_scope</code> via the{" "}
            <code>is_sandbox</code> flag in auth middleware. The frontend also
            keys off <code>role === 'demo'</code> for UI restrictions. In
            practice, demo users should have both <code>role = 'demo'</code>{" "}
            and <code>access_scope = 'sandbox'</code> set.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Row-level security (RLS)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Supabase enforces row-level security on user-facing tables, scoped
            by policy. Agents see only their own clients, saved searches, and
            email tracking. Admin operations (scraper writes, bulk updates) use
            a service-role key that bypasses RLS. Backend-only tables
            (properties, units, floor plans) rely on service-role writes. Demo
            users are further isolated through column-level filtering and
            explicit policy exclusions.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ── Notifications ── */}
      <Accordion
        expanded={expanded === "notifications"}
        onChange={handleChange("notifications")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <NotificationsIcon color="primary" />
          <Typography fontWeight={600}>Notifications</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Notifications appear via the bell icon in the top bar. Clicking a
            notification marks it as read and navigates to the relevant page.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Trigger</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Visibility</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Click action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Client Status Change</TableCell>
                  <TableCell>
                    Agent moves a client to a new pipeline stage
                  </TableCell>
                  <TableCell>
                    <Chip label="Team" size="small" color="info" />
                  </TableCell>
                  <TableCell>Opens client detail drawer</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Invoice Sent</TableCell>
                  <TableCell>
                    Invoice email successfully delivered to client
                  </TableCell>
                  <TableCell>
                    <Chip label="Team" size="small" color="info" />
                  </TableCell>
                  <TableCell>Opens invoice detail</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Invoice Failed</TableCell>
                  <TableCell>
                    Invoice email or PDF generation failed
                  </TableCell>
                  <TableCell>
                    <Chip label="Admin" size="small" color="warning" />
                  </TableCell>
                  <TableCell>Opens invoice detail</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Scrape Guard</TableCell>
                  <TableCell>
                    Pre-send freshness refresh failed for one or more properties
                  </TableCell>
                  <TableCell>
                    <Chip label="Admin" size="small" color="warning" />
                  </TableCell>
                  <TableCell>Opens Admin dashboard</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1.5, display: "block" }}
          >
            "Mark all as read" clears the unread badge. Read status is
            per-user &mdash; marking yours read does not affect other users.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ── Property Data & Freshness ── */}
      <Accordion
        expanded={expanded === "freshness"}
        onChange={handleChange("freshness")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <RefreshIcon color="primary" />
          <Typography fontWeight={600}>Property Data & Freshness</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            Data sources
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Property data (floor plans, units, pricing, specials) is scraped
            from apartment community websites and stored in Supabase. Two
            scraping methods exist:
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Chip label="Local Batch" size="small" variant="outlined" />
            <Typography variant="body2" color="text.secondary">
              Weekly run on a Windows workstation using real Chrome. Highest
              success rate (~96%). Handles bot-protected sites.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Chip label="Lambda" size="small" variant="outlined" />
            <Typography variant="body2" color="text.secondary">
              On-demand refresh via AWS Lambda (headless Chromium). ~50% success
              rate due to bot detection. Used for real-time freshness checks
              before sending emails.
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            Freshness indicators
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Meaning</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Chip label="Fresh" size="small" color="success" />
                  </TableCell>
                  <TableCell>
                    Data scraped within the last 24 hours
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="Stale" size="small" color="warning" />
                  </TableCell>
                  <TableCell>
                    Data older than 24 hours; refresh recommended before sending
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="No Data" size="small" color="error" />
                  </TableCell>
                  <TableCell>
                    No scrape record found. Property may be new, bot-blocked, or
                    have a broken parser.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            What the scraper updates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Each scrape run can update the following data:
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Table</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Updated by scraper</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>properties</TableCell>
                  <TableCell>
                    <strong>specials</strong> and <strong>phone</strong> only.
                    All other fields (name, address, commission, etc.) are
                    manually managed and never overwritten.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>floor_plans</TableCell>
                  <TableCell>
                    Full upsert &mdash; model name, bed/bath, sqft, price
                    ranges, images. New floor plans are added, existing ones
                    updated.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>units</TableCell>
                  <TableCell>
                    Full upsert &mdash; unit number, price, sqft, availability
                    date, status. Reflects real-time availability.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>scrape_metadata</TableCell>
                  <TableCell>
                    Freshness tracking &mdash; last_scraped timestamp, scrape
                    status, consecutive failure count.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>scrape_runs</TableCell>
                  <TableCell>
                    Execution log &mdash; duration, source, floor plans/units
                    changed count, failure details.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* ── Search & Sending ── */}
      <Accordion
        expanded={expanded === "search"}
        onChange={handleChange("search")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <SearchIcon color="primary" />
          <Typography fontWeight={600}>Search & Sending</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The Search page finds properties matching a client's criteria and
            shows real-time availability. Results include floor plans, unit
            counts, pricing, images, and specials.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Available filters
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Filter</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ["Neighborhood", "Filter by one or more Atlanta neighborhoods"],
                  ["Management company", "Populated from the property_management field"],
                  ["Bed / bath count", "Min and max bedroom and bathroom count"],
                  ["Budget range", "Min and max monthly rent"],
                  ["Sqft range", "Min and max square footage"],
                  ["Price per sqft", "Advanced filter for $/sqft comparison"],
                  ["Available by date", "Only show units available before a specific date"],
                  ["Available only", "Hide properties with zero available units"],
                  ["Sort by", "Price, sqft, neighborhood, or availability"],
                ].map(([filter, desc]) => (
                  <TableRow key={filter}>
                    <TableCell>{filter}</TableCell>
                    <TableCell>{desc}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Selection mode
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search defaults to floor plan selection mode. Agents select entire
            floor plans to include in a listing email. Admins can toggle to
            unit selection mode via a switch in the search toolbar, which
            allows selecting individual units instead.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Saved search compatibility
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Saved searches store the active selection mode and selected items.
            If a non-admin loads a saved search that used unit selection mode,
            it is automatically downgraded to floor plan mode.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ── Email System ── */}
      <Accordion
        expanded={expanded === "email"}
        onChange={handleChange("email")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <EmailIcon color="primary" />
          <Typography fontWeight={600}>Email System</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All outbound email is sent through the agent's connected Gmail
            account. This includes listing emails, guest cards, lease emails,
            and invoices. Every outbound message has open and click tracking
            attached automatically.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            Email providers
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Used for</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Sent from</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Chip label="Gmail OAuth" size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    Listing emails, guest cards, lease emails, invoices
                  </TableCell>
                  <TableCell>Agent's connected Gmail address</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Gmail connection
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Agents connect their Gmail in <strong>Settings &gt; Integrations</strong>.
            The OAuth flow only requests the <code>gmail.send</code>{" "}
            permission &mdash; the app cannot read your inbox or contacts. Key
            details:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Tokens auto-refresh in the background (no manual re-auth needed)
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Disconnecting revokes access at Google immediately
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Gmail must be connected to send any email (listings, guest
              cards, lease emails, invoices)
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Guest cards
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Guest cards are sent to a property's leasing office to introduce a
            client. They include the client's name, phone, email, budget,
            desired move-in date, and bed/bath preferences, along with the
            locator's contact information. Sent from the agent's connected
            Gmail as a formatted HTML email.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ── Email Tracking ── */}
      <Accordion
        expanded={expanded === "tracking"}
        onChange={handleChange("tracking")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <EmailIcon color="primary" />
          <Typography fontWeight={600}>Email Tracking</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Every outbound email (listing emails, guest cards, lease emails,
            and invoices) is automatically instrumented with open and click
            tracking. Tracking is non-blocking &mdash; if tracking injection
            fails, the email still sends without tracking.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            How it works
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Chip label="Open tracking" size="small" variant="outlined" />
            <Typography variant="body2" color="text.secondary">
              A transparent tracking pixel is embedded in each email. When the
              recipient's email client loads it, an open event is recorded
              with a timestamp.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Chip label="Click tracking" size="small" variant="outlined" />
            <Typography variant="body2" color="text.secondary">
              Links in the email are rewritten to pass through a tracking
              redirect. When clicked, the original URL is resolved
              server-side and the recipient is redirected. Original URLs are
              stored server-side and never exposed in tracking links.
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            Badge tiers
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Badge</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Meaning</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Chip label="Clicked" size="small" color="success" />
                  </TableCell>
                  <TableCell>
                    Recipient clicked at least one link. Strongest signal of
                    engagement.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="Opened" size="small" color="success" />
                  </TableCell>
                  <TableCell>
                    Tracking pixel fired multiple times more than two minutes
                    apart, indicating the recipient returned to the email.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="Delivered" size="small" color="warning" />
                  </TableCell>
                  <TableCell>
                    Tracking pixel fired, but could be a real open or an
                    automated image prefetch by Gmail or Apple Mail Privacy
                    Protection.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="Sent" size="small" />
                  </TableCell>
                  <TableCell>
                    Email sent less than 24 hours ago. No opens detected yet.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="Not opened" size="small" color="error" />
                  </TableCell>
                  <TableCell>
                    Email sent more than 24 hours ago with no opens detected.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Where badges appear
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tracking badges appear in the client detail drawer across four
            tabs: Activity history, Sent Listings, Guest Cards, and Invoices.
            Lease upload email tracking appears in the Activity history tab
            (there is no dedicated lease tab).
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Accuracy and privacy
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Clicks are the strongest evidence of engagement &mdash; they
              require an intentional user action.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              "Opened" requires repeated pixel loads across separate sessions
              (more than two minutes apart) to distinguish real engagement
              from automated prefetching.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              "Delivered" is intentionally ambiguous. Gmail's image proxy and
              Apple Mail Privacy Protection can prefetch the tracking pixel at
              delivery time, making a single pixel fire indistinguishable from
              a real open.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Security scanners (Outlook Safe Links, Barracuda, etc.) that
              pre-click links within seconds of delivery are detected and
              excluded from badge calculation.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Tracking failure never blocks sending. If pixel injection or
              link rewriting fails, the email is sent unmodified.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ── Invoice System ── */}
      <Accordion
        expanded={expanded === "invoices"}
        onChange={handleChange("invoices")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <InvoiceIcon color="primary" />
          <Typography fontWeight={600}>Invoice System</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Invoices are generated as PDFs and emailed to clients via the
            agent's connected Gmail. The PDF is created by an AWS Lambda
            function and stored in S3.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            Invoice lifecycle
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Step</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>What happens</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ["1. Create", "Invoice record saved to database with auto-incremented invoice number"],
                  ["2. Generate PDF", "AWS Lambda renders PDF with invoice details, stores in S3"],
                  ["3. Store", "PDF downloaded from S3 and stored as base64 in the database for quick retrieval"],
                  ["4. Email", "Sent via agent's Gmail OAuth with PDF attached"],
                  ["5. Track", "Status updated, activity event logged, notification created"],
                ].map(([step, desc]) => (
                  <TableRow key={step}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{step}</TableCell>
                    <TableCell>{desc}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            Invoice statuses
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Chip label="Draft" size="small" variant="outlined" />
            <Chip label="Sent" size="small" color="info" />
            <Chip label="Paid" size="small" color="success" />
            <Chip label="Overdue" size="small" color="warning" />
            <Chip label="Cancelled" size="small" />
            <Chip label="Failed" size="small" color="error" />
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Requirements
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Gmail must be connected (invoices are sent from your Gmail)
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Invoice details: client info, property, base rent, commission %,
              move-in date
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              PDF includes company info (Real McCoy Realty Consultants), Net 30
              terms
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ── Client Management ── */}
      <Accordion
        expanded={expanded === "clients"}
        onChange={handleChange("clients")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <PeopleIcon color="primary" />
          <Typography fontWeight={600}>Client Management</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Pipeline stages
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Stage</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ["New", "Client just added, no outreach yet"],
                  ["Contacted", "Initial message or call sent"],
                  ["Touring", "Actively scheduling or completing tours"],
                  ["Applied", "Application submitted at one or more properties"],
                  ["Signed", "Lease signed, deal closed"],
                  ["Lost", "Client went with another service or ghosted"],
                ].map(([stage, desc]) => (
                  <TableRow key={stage}>
                    <TableCell>{stage}</TableCell>
                    <TableCell>{desc}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, mb: 1 }}
          >
            Changing a client's status creates a <strong>team-wide</strong>{" "}
            notification so all agents stay in the loop.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Priority levels
          </Typography>
          <Typography variant="body2" color="text.secondary">
            High, Medium, Low. Clickable directly from the client detail drawer.
            Priority is informational and does not affect sorting or automation.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ── Properties Page ── */}
      <Accordion
        expanded={expanded === "properties"}
        onChange={handleChange("properties")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <HomeIcon color="primary" />
          <Typography fontWeight={600}>Properties Page</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Property cards
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each card shows: neighborhood, floor plan count, available unit
            count, commission chips, specials summary, and favorite star.
            Favorited properties sort to the top.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Commission chips
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The prominent chip shows the <em>with tour</em> rate. Hover for a
            tooltip showing both with-tour and without-tour rates plus the
            commission type (% or flat).
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Specials summary
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Extracted automatically from scraped HTML via regex. If the
            extraction isn't clean (e.g. too long or contains HTML), the card
            shows a generic "Leasing Special" label instead.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Edit modal (admin only)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tabs: <strong>Floor Plans</strong> (read-only, from scraper) |{" "}
            <strong>Commission & Notes</strong> (editable) |{" "}
            <strong>Specials & Fees</strong> (read-only) |{" "}
            <strong>Details</strong> (address, website, phone).
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Floor plan images
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Floor plan and property images are stored in S3. When both an S3
            image (<code>s3_image_url</code>) and a scraped image (
            <code>image_url</code>) exist, the S3 version takes priority. The
            &ldquo;No Floor Plan Image&rdquo; filter in Search shows only
            properties where at least one floor plan is missing an image.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ── Lead Verification ── */}
      <Accordion
        expanded={expanded === "lead-verification"}
        onChange={handleChange("lead-verification")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <ShieldIcon color="primary" />
          <Typography fontWeight={600}>Lead Verification</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Admin-triggered identity verification for incoming leads. One
            click runs five Twilio Lookup v2 checks plus a free DNS check
            on the email domain, in parallel, against the lead's phone
            and email. The result updates the lead's quality chip
            (Unverified / Verified / Review / Flagged) and is stored for
            audit. Each click costs roughly 10&cent;, hard-capped at a
            daily spend limit (default $15/day).
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            What we check
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Check</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    What it actually asks
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    What a clean result looks like
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Phone is real &amp; type</TableCell>
                  <TableCell>
                    Does this number exist in a carrier's routing table,
                    and is it mobile, landline, or VOIP?
                  </TableCell>
                  <TableCell>
                    Mobile or landline. VOIP is a mild red flag &mdash;
                    common in spam because it's cheap and anonymous to
                    create.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Name matches phone (Identity Match)</TableCell>
                  <TableCell>
                    Do the first and last name on the lead appear on the
                    carrier's billing record for this phone?
                  </TableCell>
                  <TableCell>
                    Exact match. Strongest single signal &mdash; billing
                    records exist even when public directories don't.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Registered name (CNAM)</TableCell>
                  <TableCell>
                    Who is this phone publicly registered to &mdash; the
                    same data that appears on Caller ID?
                  </TableCell>
                  <TableCell>
                    Matches the lead. Blank on mobile is normal (carriers
                    suppress for privacy) &mdash; <strong>not</strong> a
                    red flag on its own.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Fraud-pattern check (SMS Pumping)</TableCell>
                  <TableCell>
                    Is this number flagged in Twilio's database of
                    coordinated spam / fraud patterns?
                  </TableCell>
                  <TableCell>
                    Low risk. &ldquo;Carrier blocked&rdquo; = near-certain
                    spam; don't waste locator time.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Port recency (Reassigned Number)</TableCell>
                  <TableCell>
                    When was this phone last verified to its current
                    owner?
                  </TableCell>
                  <TableCell>
                    Verified months or years ago. A very recent port is a
                    mild churn / fraud signal.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Email domain is real</TableCell>
                  <TableCell>
                    Does the email's domain have working mail servers, and
                    is the domain on our disposable-email blocklist?
                  </TableCell>
                  <TableCell>
                    Domain resolves, not disposable. Catches typos and
                    burner providers (Mailinator, TempMail, etc.).
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Name &harr; email sanity</TableCell>
                  <TableCell>
                    Does the first name appear somewhere inside the email
                    address?
                  </TableCell>
                  <TableCell>
                    Presence (&ldquo;john&rdquo; in <code>john@...</code>)
                    bumps the score slightly. Absence doesn't punish
                    &mdash; real emails often use initials or nicknames.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Reading the result
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Chip</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Meaning</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>What to do</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Chip
                      size="small"
                      label="Unverified"
                      sx={{
                        backgroundColor: "rgba(148, 163, 184, 0.15)",
                        color: "#94A3B8",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    No vendor check has run yet. The score is just
                    &ldquo;are the fields filled in?&rdquo; &mdash; can't
                    distinguish real from fake.
                  </TableCell>
                  <TableCell>
                    Click <strong>Verify</strong> before investing
                    locator time.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip
                      size="small"
                      label="Verified"
                      sx={{
                        backgroundColor: "rgba(16, 185, 129, 0.15)",
                        color: "#10B981",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    All strong signals aligned: phone is real, name
                    matches carrier billing, no fraud flags, email is
                    deliverable.
                  </TableCell>
                  <TableCell>
                    Proceed with confidence &mdash; this is a real
                    person.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip
                      size="small"
                      label="Review"
                      sx={{
                        backgroundColor: "rgba(245, 158, 11, 0.15)",
                        color: "#F59E0B",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    Mixed signals &mdash; often a VOIP number with
                    otherwise-clean data, or a partial name match.
                  </TableCell>
                  <TableCell>
                    Open the popover, scan the flags, ask a qualifying
                    question on first contact.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip
                      size="small"
                      label="Flagged"
                      sx={{
                        backgroundColor: "rgba(239, 68, 68, 0.15)",
                        color: "#EF4444",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    Hard fails: fake phone, undeliverable email,
                    disposable domain, or carrier-blocked for fraud.
                  </TableCell>
                  <TableCell>
                    Don't invest locator time. Archive unless you have
                    strong other reasons to engage.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Internal scoring weights (for QA)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Final score is clamped to 0&ndash;100. <strong>HIGH</strong>{" "}
            &ge; 80, <strong>MID</strong> 50&ndash;79,{" "}
            <strong>LOW</strong> &lt; 50. Each bucket maps to the
            Verified / Review / Flagged chip.
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Signal</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Positive</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Negative</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Name &harr; phone (CNAM)</TableCell>
                  <TableCell>+15 exact / +5 partial</TableCell>
                  <TableCell>&minus;10 mismatch</TableCell>
                  <TableCell>Combined with IM, negative delta capped at &minus;20</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Name &harr; phone (Identity Match)</TableCell>
                  <TableCell>+20 exact / +5 partial</TableCell>
                  <TableCell>&minus;15 mismatch</TableCell>
                  <TableCell>Combined with CNAM, negative delta capped at &minus;20</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Phone valid</TableCell>
                  <TableCell>+10</TableCell>
                  <TableCell>&minus;15</TableCell>
                  <TableCell>&mdash;</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Line type = mobile</TableCell>
                  <TableCell>+5</TableCell>
                  <TableCell>&mdash;</TableCell>
                  <TableCell>&mdash;</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Line type = VOIP</TableCell>
                  <TableCell>&mdash;</TableCell>
                  <TableCell>&minus;15</TableCell>
                  <TableCell>Common in spam signups</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>SMS pumping = carrier_block</TableCell>
                  <TableCell>&mdash;</TableCell>
                  <TableCell>&minus;30</TableCell>
                  <TableCell>Carrier confirmed fraud</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>SMS pumping = high</TableCell>
                  <TableCell>&mdash;</TableCell>
                  <TableCell>&minus;20</TableCell>
                  <TableCell>&mdash;</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>SMS pumping = medium</TableCell>
                  <TableCell>&mdash;</TableCell>
                  <TableCell>&minus;5</TableCell>
                  <TableCell>&mdash;</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Email deliverable</TableCell>
                  <TableCell>+10</TableCell>
                  <TableCell>&minus;25</TableCell>
                  <TableCell>Biggest single email penalty</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Email disposable</TableCell>
                  <TableCell>&mdash;</TableCell>
                  <TableCell>&minus;20</TableCell>
                  <TableCell>Mailinator, TempMail, etc.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Phone reassigned &lt; 90 days</TableCell>
                  <TableCell>&mdash;</TableCell>
                  <TableCell>&minus;5</TableCell>
                  <TableCell>Mild churn signal</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Cost &amp; spend cap
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each Verify click runs a single Twilio Lookup v2 call with
            five packages (line type, caller name, SMS pumping risk,
            identity match, reassigned number) plus a free DNS check for
            the email domain. Twilio bills roughly 9.6&cent; per call
            &mdash; rounded up to <strong>10&cent;</strong> in our
            tracking for safety. A <strong>daily spend cap</strong>{" "}
            (default $15/day, set via{" "}
            <code>LEAD_VERIFICATION_DAILY_CAP_CENTS</code>) prevents
            runaway spend if an admin account is compromised. Hit the cap
            and the endpoint returns HTTP 402 until midnight UTC. Today's
            running spend and the last 7 days live in the{" "}
            <strong>Verification spend</strong> tile on the admin
            dashboard.
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            When a check comes back &ldquo;unavailable&rdquo;
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Some checks return <strong>unavailable</strong> even on a
            successful verify &mdash; usually because the underlying
            carrier didn't publish that data (common on mobile for CNAM),
            Twilio's Identity Match has a regional gap, or the lead never
            gave us an email. Unavailable is <strong>neutral</strong>
            &mdash; it doesn't add or subtract from the score. It just
            means we couldn't check that dimension. A lead with two
            strong positive signals and three unavailable ones can still
            land in <strong>Verified</strong>.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ── Admin Dashboard ── */}
      <Accordion
        expanded={expanded === "admin"}
        onChange={handleChange("admin")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <AdminIcon color="primary" />
          <Typography fontWeight={600}>Admin Dashboard</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Admin-only page showing scraper pipeline health and recent run
            history.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Measured state
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Counted values, failed reads, and sources with no writer render as
            distinct states. Missing data never falls back to zero.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Property Health table
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Lists recent scrape runs with status, source (Lambda vs Local),
            floor plan/unit counts, and duration. Searchable. Click a row to
            open a detail modal with run history.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Source badges
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Chip label="Lambda" size="small" color="secondary" />
            <Typography variant="body2" color="text.secondary">
              Run triggered on-demand via AWS Lambda (headless).
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Chip label="Local" size="small" variant="outlined" />
            <Typography variant="body2" color="text.secondary">
              Run from the weekly batch on the local workstation (real Chrome).
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Freshness guard modes
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Mode</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Behavior</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Pass-through</TableCell>
                  <TableCell>
                    No freshness checks; emails sent regardless of data age
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Block All</TableCell>
                  <TableCell>
                    All sends blocked (maintenance mode)
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* ── AWS Infrastructure ── */}
      <Accordion
        expanded={expanded === "aws"}
        onChange={handleChange("aws")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <CloudIcon color="primary" />
          <Typography fontWeight={600}>AWS Infrastructure</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The backend offloads heavy or specialized tasks to AWS services.
            Each service uses its own IAM credentials for security isolation.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            Hosting
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Backend</TableCell>
                  <TableCell>
                    <Chip label="Render" size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>Python FastAPI application</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Frontend</TableCell>
                  <TableCell>
                    <Chip label="Vercel" size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>React SPA with static build output</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            AWS services
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Function</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Triggered by</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Chip label="Lambda" size="small" color="secondary" />
                  </TableCell>
                  <TableCell>Scraper Refresh</TableCell>
                  <TableCell>
                    Pre-send freshness check, admin refresh button
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="Lambda" size="small" color="secondary" />
                  </TableCell>
                  <TableCell>Invoice PDF Generation</TableCell>
                  <TableCell>
                    Invoice send flow (renders PDF, returns S3 key)
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="S3" size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>Invoice PDF Storage</TableCell>
                  <TableCell>
                    Invoice Lambda stores PDFs; backend retrieves for email
                    attachment
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Chip label="S3" size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>Property Image Storage</TableCell>
                  <TableCell>
                    Scraper uploads property and floor plan images
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Retry & error handling
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lambda invocations use retry logic with exponential backoff (up to 3
            attempts). If all retries fail, the operation is marked as failed, a
            notification is created, and the user is informed. The scraper
            Lambda processes properties in configurable chunks (default: 3, max:
            20) to manage concurrency.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Key configuration references
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <code>ALLOWED_ORIGINS</code> (CORS),{" "}
            <code>EMAIL_TRACKING_BASE_URL</code> (permanent tracking
            pixel/click base &mdash; must not change once emails are sent),{" "}
            <code>SUPABASE_SERVICE_KEY</code> (service-role access bypassing
            RLS).
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ── Caching ── */}
      <Accordion
        expanded={expanded === "caching"}
        onChange={handleChange("caching")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <SpeedIcon color="primary" />
          <Typography fontWeight={600}>Caching & Performance</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The app uses React Query to cache API responses client-side,
            reducing Supabase calls and making page navigation instant after
            first load.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Stale time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>GC time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    Invalidated by
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Properties (enriched, stats, neighborhoods)</TableCell>
                  <TableCell>24 hours</TableCell>
                  <TableCell>1 hour</TableCell>
                  <TableCell>Save, favorite toggle, refresh button</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Admin scrape runs</TableCell>
                  <TableCell>1 hour</TableCell>
                  <TableCell>30 min</TableCell>
                  <TableCell>Admin refresh button</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Notifications</TableCell>
                  <TableCell>30 seconds</TableCell>
                  <TableCell>5 min</TableCell>
                  <TableCell>Auto-refetch on window focus</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Client data</TableCell>
                  <TableCell>Not cached</TableCell>
                  <TableCell>&mdash;</TableCell>
                  <TableCell>Fetched fresh on every page load</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1.5, display: "block" }}
          >
            "Stale time" = how long before data is considered outdated and
            refetched in the background. "GC time" = how long unused cache
            entries are kept in memory.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ── Admin-gated features ── */}
      <Accordion
        expanded={expanded === "admin-gates"}
        onChange={handleChange("admin-gates")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <StarIcon color="primary" />
          <Typography fontWeight={600}>Admin-Gated Features</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following features are only visible or actionable for users with
            the <strong>admin</strong> role.
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Feature</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ["Admin Dashboard page", "Sidebar navigation"],
                  ["Property edit pencil", "Properties page cards"],
                  ["Data export", "Settings > Data & Privacy"],
                  ["Admin-only notifications", "Bell icon dropdown"],
                  ["This System Guide tab", "Settings"],
                ].map(([feature, location]) => (
                  <TableRow key={feature}>
                    <TableCell>{feature}</TableCell>
                    <TableCell>{location}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* ── Demo / Sandbox Mode ── */}
      <Accordion
        expanded={expanded === "demo"}
        onChange={handleChange("demo")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <VisibilityIcon color="primary" />
          <Typography fontWeight={600}>Demo / Sandbox Mode</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Demo mode provides a self-contained sandbox for evaluating the app
            without affecting production data. Demo users see 3 seeded clients
            and a filtered set of demo-visible properties.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            Guardrails
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Feature</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    Behavior in demo
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ["Gmail connect", "Blocked (403)"],
                  [
                    "Email sending (listings, guest cards, leases)",
                    "Blocked (403)",
                  ],
                  [
                    "Invoice send",
                    "Returns a demo_preview response with placeholder data — no Lambda, no DB record, no email",
                  ],
                  [
                    "Market Insights",
                    "Not accessible (filtered from nav + redirected)",
                  ],
                  [
                    "Settings",
                    "Not accessible (filtered from nav + redirected)",
                  ],
                  [
                    "Saved searches",
                    "Read-only (create/update/delete blocked)",
                  ],
                  [
                    "Notifications",
                    "Static demo notifications seeded on reset (not live)",
                  ],
                  [
                    "Properties",
                    "Filtered to demo_visible = true only",
                  ],
                  [
                    "Guest card properties",
                    "Filtered to demo-visible properties",
                  ],
                ].map(([feature, behavior]) => (
                  <TableRow key={feature}>
                    <TableCell>{feature}</TableCell>
                    <TableCell>{behavior}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Demo reset
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <code>POST /admin/reset-demo</code> resets demo-owned workflow data
            only while its clients have no durable stage history. Once immutable
            history exists, the endpoint returns an explicit 409 before deleting
            anything and directs the operator to use a fresh sandbox demo identity.
            Stage-history rows are never deleted or detached.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Demo clients
          </Typography>
          <Typography variant="body2" color="text.secondary">
            3 demo clients are seeded on reset, each with pre-populated
            preferences, pipeline stages, and activity history.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* ── Security & Rate Limits ── */}
      <Accordion
        expanded={expanded === "security"}
        onChange={handleChange("security")}
        sx={sectionSx}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={headerSx}>
          <SecurityIcon color="primary" />
          <Typography fontWeight={600}>Security & Rate Limits</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The backend enforces rate limits, security headers, and CORS
            restrictions to protect against abuse.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }} fontWeight={600}>
            Rate limits
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Scope</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Limit</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    Configured via
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  [
                    "Invoice preview / send",
                    "10/hour per session",
                    "RATE_LIMIT_INVOICE",
                  ],
                  [
                    "Gmail send",
                    "20/hour per session",
                    "RATE_LIMIT_EMAIL",
                  ],
                  [
                    "Email tracking (open pixel)",
                    "120/minute",
                    "Hardcoded",
                  ],
                  [
                    "Email tracking (click redirect)",
                    "60/minute",
                    "Hardcoded",
                  ],
                  ["Demo login", "5/minute", "Hardcoded"],
                ].map(([scope, limit, config]) => (
                  <TableRow key={scope}>
                    <TableCell>{scope}</TableCell>
                    <TableCell>{limit}</TableCell>
                    <TableCell>
                      <code>{config}</code>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Rate limits use in-memory storage (slowapi) and reset on deploy.
            Per-session identification uses the bearer token suffix, falling
            back to client IP when unauthenticated.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            Security headers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All responses include:{" "}
            <code>X-Content-Type-Options: nosniff</code>,{" "}
            <code>X-Frame-Options: DENY</code>,{" "}
            <code>X-XSS-Protection: 1; mode=block</code>,{" "}
            <code>Referrer-Policy: strict-origin-when-cross-origin</code>.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" sx={{ mb: 1 }} fontWeight={600}>
            CORS
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Origins are restricted via <code>ALLOWED_ORIGINS</code> env var
            (comma-separated). In development, localhost origins are allowed as
            a fallback. Production requires explicit origin configuration.
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default SystemGuideTab;
